import React, { useRef, useEffect, useCallback } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { injectIntl } from 'react-intl';
import _ from 'lodash';
import './PortfolioChart.scss';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { customLegendCallback } from '../../../../../utils/chart-js';
import { getWebImageUrl } from '../../../../../utils/image';
import AddIcon from '@mui/icons-material/Add';
import { decimalToString } from '../../../../../utils/formatNumber';
import ResizeObserver from 'resize-observer-polyfill';

const PortfolioChart = props => {
  const chartGridRef = useRef();
  const chartCanvasWrapperRef = useRef();
  const chartRef = useRef();
  const legendRef = useRef();

  const {
    intl,
    children,
    currency,
    chartData,
    style,
    legendWrapperProps,
    title,
    chartHeight,
    updateHeight,
    isProfolioChartShown,
    addPolicy,
    ...rest
  } = props;

  const syncChartLayout = useCallback(() => {
    const legendDiv = _.get(legendRef, 'current');
    const chartInstance = _.get(chartRef, 'current.chartInstance');
    if (chartInstance && chartInstance.resize) {
      chartInstance.resize();
    }
    if (legendDiv && chartInstance) {
      legendDiv.innerHTML = chartInstance.generateLegend();
    }

    const chartGridDiv = _.get(chartGridRef, 'current');
    if (updateHeight && chartGridDiv && legendDiv) {
      updateHeight(chartGridDiv.offsetHeight + legendDiv.offsetHeight + 26);
    }
  }, [updateHeight]);

  useEffect(() => {
    syncChartLayout();
  }, [syncChartLayout, chartData, isProfolioChartShown, title, currency]);

  useEffect(() => {
    const chartGridDiv = _.get(chartGridRef, 'current');
    const chartCanvasWrapperDiv = _.get(chartCanvasWrapperRef, 'current');
    const resizeObserver = new ResizeObserver(syncChartLayout);
    if (chartGridDiv) {
      resizeObserver.observe(chartGridDiv);
    }
    if (chartCanvasWrapperDiv) {
      resizeObserver.observe(chartCanvasWrapperDiv);
    }

    const delayedSyncTimers = [100, 300, 600].map(delay => setTimeout(syncChartLayout, delay));
    window.addEventListener('resize', syncChartLayout, false);
    window.addEventListener('orientationchange', syncChartLayout, false);

    return () => {
      delayedSyncTimers.forEach(timer => clearTimeout(timer));
      window.removeEventListener('resize', syncChartLayout, false);
      window.removeEventListener('orientationchange', syncChartLayout, false);
      resizeObserver.disconnect();
    };
  }, [syncChartLayout]);

  const chartConfig = {
    cutoutPercentage: 78,
    legend: {
      display: false
    },
    legendCallback: customLegendCallback('portfolio-chart-legend'),
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false
      }
    },
    tooltips: {
      enabled: true,
      callbacks: {
        label: function(tooltipItem, data) {
          var label = data.labels[tooltipItem.index] || '';
          var currentValue = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];

          if (label !== intl.formatMessage({ id: 'No Policy' })) {
            if (label) {
              label += ': ';
            }
            label += intl.formatMessage({ id: currency }) + ' ';
            label += currentValue === '0.1' ? '0' : decimalToString(currentValue, 0);
          }

          return label;
        }
      }
    }
  };

  return (
    <div className="portfolio-chart" style={style} {...rest}>
      <div>
        <div className="Outer">
          {chartData && isProfolioChartShown && (
            <Grid
              container
              wrap="nowrap"
              style={{ height: '100%' }}
              sx={{ flexDirection: 'column', alignItems: 'center' }}
            >
              <Grid sx={{ flexShrink: 0 }}>
                {title && (
                  <Typography
                    color="textSecondary"
                    align="center"
                    style={{ fontSize: '125%', fontWeight: '600', marginBottom: '5px' }}
                  >
                    {title}
                  </Typography>
                )}
              </Grid>
              <Grid
                style={{
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                ref={chartGridRef}
              >
                {/* Flex sizing (not height: 100%) so iOS Safari resolves the canvas box —
                    WebKit won't resolve a percentage height against a flex: 1 item. */}
                <div
                  style={{
                    position: 'relative',
                    padding: '0 10%',
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div className="portfolio-chart-canvas-wrapper" ref={chartCanvasWrapperRef}>
                    <Doughnut data={chartData} options={chartConfig} ref={chartRef} />
                  </div>
                </div>
              </Grid>
              <Grid sx={{ flexShrink: 0 }}>
                <div {...legendWrapperProps} ref={legendRef} />
              </Grid>
            </Grid>
          )}

          {!isProfolioChartShown && (
            <Grid
              container
              wrap="nowrap"
              style={{ height: '100%' }}
              onClick={addPolicy}
              sx={{ flexDirection: 'column', alignItems: 'center' }}
            >
              <Grid sx={{ flexShrink: 0 }}>
                <Typography
                  color="textSecondary"
                  align="center"
                  style={{ fontSize: '125%', fontWeight: '600', marginBottom: '5px' }}
                >
                  {intl.formatMessage({ id: 'have-no-policy' })}
                </Typography>
              </Grid>
              <Grid
                style={{
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                ref={chartGridRef}
              >
                <div
                  style={{
                    position: 'relative',
                    padding: '0 10%',
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div className="portfolio-chart-canvas-wrapper">
                    <img
                      src={getWebImageUrl('img/exclamation.png')}
                      alt="exclamation"
                      style={{ maxWidth: '80%', maxHeight: '80%', paddingTop: '5%' }}
                    />
                  </div>
                </div>
              </Grid>
              <Grid
                container
                className="portfolio-chart-add-policy"
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Grid>
                  <AddIcon color="primary" onClick={() => addPolicy()} />
                </Grid>
                <Grid>
                  <Typography
                    color="textSecondary"
                    align="center"
                    style={{ fontSize: '125%', fontWeight: '600', marginBottom: 0 }}
                  >
                    {intl.formatMessage({ id: 'add-policies' })}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
        </div>
      </div>
      {children}
    </div>
  );
};

export default injectIntl(PortfolioChart);
